import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
    BellRing, Check, CheckCheck, Clock, X, Inbox, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Topbar notification bell with slide-in panel.
 *
 * - Polls `/api/notifications?unread_only=true` every 90s for the count.
 * - Clicking the bell opens a right-side slide-in panel showing the 20
 *   most-recent notifications for the caller (all, not just unread).
 * - Actions: mark-one-read, mark-all-read, snooze 24h, dismiss, jump-to
 *   the action_url in the notification.
 *
 * Kept intentionally lean — powered entirely by the existing
 * `routes/notifications.py` backend, no new endpoints introduced.
 */
export default function NotificationBell() {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const panelRef = useRef(null);

    // Poll unread count
    const fetchCount = async () => {
        try {
            const r = await api.get("/notifications?unread_only=true&limit=1");
            setUnread(Number(r.data?.unread || 0));
        } catch { /* silent */ }
    };

    useEffect(() => {
        fetchCount();
        const iv = setInterval(fetchCount, 90_000);
        return () => clearInterval(iv);
    }, []);

    // Fetch rows on open
    const loadRows = async () => {
        setBusy(true);
        try {
            const r = await api.get("/notifications?limit=20");
            setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
            setUnread(Number(r.data?.unread || 0));
        } catch { /* silent */ }
        finally { setBusy(false); }
    };

    useEffect(() => {
        if (open) loadRows();
    }, [open]);

    // Close on outside click / Esc
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        const onClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        setTimeout(() => document.addEventListener("mousedown", onClick), 0);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClick);
        };
    }, [open]);

    const markRead = async (id) => {
        try {
            await api.post(`/notifications/${id}/read`);
            setRows((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
            setUnread((n) => Math.max(0, n - 1));
        } catch { toast.error("Couldn't mark as read"); }
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setRows((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
            setUnread(0);
            toast.success("All notifications marked read");
        } catch { toast.error("Couldn't mark all read"); }
    };

    const snooze = async (id) => {
        try {
            await api.post(`/notifications/${id}/snooze`, { hours: 24 });
            setRows((prev) => prev.filter((n) => n.id !== id));
            toast.success("Snoozed for 24 hours");
        } catch { toast.error("Couldn't snooze"); }
    };

    const dismiss = async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            setRows((prev) => prev.filter((n) => n.id !== id));
        } catch { toast.error("Couldn't dismiss"); }
    };

    const openAction = async (n) => {
        if (!n.read_at) markRead(n.id);
        setOpen(false);
        if (n.action_url) nav(n.action_url);
        else nav("/alerts");
    };

    const sevClass = (sev) => ({
        critical: "bg-rose-500",
        high: "bg-amber-500",
        med: "bg-blue-500",
        low: "bg-stone-400",
        info: "bg-stone-400",
    }[sev] || "bg-stone-400");

    const relTime = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
        return d.toLocaleDateString();
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="relative w-9 h-9 inline-flex items-center justify-center border border-stone-200 rounded-full text-stone-600 hover:border-stone-300 hover:text-stone-900 transition-colors"
                data-testid="topbar-alerts-btn"
                aria-label="Notifications"
            >
                <BellRing size={15} />
                {unread > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold inline-flex items-center justify-center"
                        data-testid="topbar-alert-count"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="fixed inset-0 z-50 bg-stone-900/20 backdrop-blur-sm" data-testid="notification-panel-overlay">
                    <div
                        ref={panelRef}
                        className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-stone-200 flex flex-col animate-in slide-in-from-right duration-200"
                        data-testid="notification-panel"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">VAULT Alerts</div>
                                <h2 className="text-lg font-semibold text-stone-900 mt-0.5">
                                    Notifications {unread > 0 && <span className="text-amber-700 text-sm font-medium">· {unread} unread</span>}
                                </h2>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-stone-200 text-stone-500 hover:border-stone-300 hover:text-stone-900"
                                data-testid="notification-panel-close"
                                aria-label="Close notifications"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Actions row */}
                        <div className="flex items-center gap-2 px-5 py-2 border-b border-stone-200 bg-stone-50">
                            <button
                                onClick={markAllRead}
                                disabled={unread === 0}
                                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-stone-700 hover:text-stone-900 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="notification-mark-all-read"
                            >
                                <CheckCheck size={12} /> Mark all read
                            </button>
                            <div className="flex-1" />
                            <button
                                onClick={() => { setOpen(false); nav("/alerts"); }}
                                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-stone-700 hover:text-stone-900"
                                data-testid="notification-view-all"
                            >
                                View all <ExternalLink size={11} />
                            </button>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto" data-testid="notification-list">
                            {busy && rows.length === 0 ? (
                                <div className="p-8 text-center text-sm text-stone-400">Loading…</div>
                            ) : rows.length === 0 ? (
                                <div className="p-12 text-center" data-testid="notification-empty">
                                    <div className="w-14 h-14 rounded-full bg-stone-100 mx-auto mb-3 flex items-center justify-center">
                                        <Inbox size={20} className="text-stone-400" />
                                    </div>
                                    <div className="text-sm font-semibold text-stone-900">You&apos;re all caught up.</div>
                                    <div className="text-[12px] text-stone-500 mt-1">
                                        No notifications right now. We&apos;ll ping you when something needs attention.
                                    </div>
                                </div>
                            ) : rows.map((n) => (
                                <div
                                    key={n.id}
                                    className={`group border-b border-stone-100 px-5 py-3.5 hover:bg-stone-50 transition-colors ${!n.read_at ? "bg-amber-50/30" : ""}`}
                                    data-testid={`notification-row-${n.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${sevClass(n.severity)}`} aria-hidden="true" />
                                        <div className="flex-1 min-w-0">
                                            <button
                                                onClick={() => openAction(n)}
                                                className="text-left w-full"
                                                data-testid={`notification-open-${n.id}`}
                                            >
                                                <div className="text-[13.5px] font-semibold text-stone-900 leading-tight">
                                                    {n.title}
                                                </div>
                                                {n.body && (
                                                    <div className="text-[12px] text-stone-600 mt-1 line-clamp-2">
                                                        {n.body}
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-2 mt-1.5 text-[10.5px] text-stone-400 uppercase tracking-wider">
                                                    <span>{n.category}</span>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1 normal-case tracking-normal">
                                                        <Clock size={9} /> {relTime(n.created_at)}
                                                    </span>
                                                </div>
                                            </button>
                                            <div className="flex items-center gap-3 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {!n.read_at && (
                                                    <button
                                                        onClick={() => markRead(n.id)}
                                                        className="text-[11px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                                                        data-testid={`notification-mark-read-${n.id}`}
                                                    >
                                                        <Check size={11} /> Mark read
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => snooze(n.id)}
                                                    className="text-[11px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                                                    data-testid={`notification-snooze-${n.id}`}
                                                >
                                                    <Clock size={11} /> Snooze 24h
                                                </button>
                                                <button
                                                    onClick={() => dismiss(n.id)}
                                                    className="text-[11px] text-stone-500 hover:text-rose-600 inline-flex items-center gap-1 ml-auto"
                                                    data-testid={`notification-dismiss-${n.id}`}
                                                >
                                                    <X size={11} /> Dismiss
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="border-t border-stone-200 px-5 py-3 bg-stone-50">
                            <button
                                onClick={() => { setOpen(false); nav("/settings#notifications"); }}
                                className="text-[11px] text-stone-500 hover:text-stone-900 transition-colors"
                                data-testid="notification-preferences"
                            >
                                Manage notification preferences →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
